import { createRequest } from '@/services/request';
import { BizAppConfig } from 'config/application.manifest';
import type { ApiRequestOptions } from './types/common';
import type {
  CurrentMember,
  MemberListItem,
  MemberListResponse,
} from './types/member';
import type {
  CurrentUser,
  CreateTenantPayload,
  CreateTenantResult,
  DeleteTenantResult,
  ExchangeCodeForTokenPayload,
  JoinTenantPayload,
  JoinTenantResult,
  LogoutResult,
  OAuth2TokenResult,
  SwitchTenantResult,
  TenantByCodeResult,
  TenantContext,
} from './types/auth';

const AUTH_PREFIX = process.env.UMI_APP_AUTH_BFF_URL || '';
const TENANT_MANAGEMENT_PREFIX =
  BizAppConfig.appTenantManagementUrl ||
  process.env.UMI_APP_TENANT_MANAGEMENT_URL ||
  '';

const request = <T>(
  url: string,
  options?: ApiRequestOptions,
) => createRequest(AUTH_PREFIX)<T>(url, options);

const tenantManagementRequest = <T>(
  url: string,
  options?: ApiRequestOptions,
) => createRequest(TENANT_MANAGEMENT_PREFIX)<T>(url, options);

const pickFirstNonEmpty = (...values: Array<string | undefined>) =>
  values.find((value) => typeof value === 'string' && value.trim());

const pickMemberList = (
  payload: MemberListResponse | MemberListItem[] | undefined,
): MemberListItem[] => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  const candidates = [
    payload.items,
    payload.records,
    payload.list,
    payload.data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (candidate && !Array.isArray(candidate)) {
      const nestedMembers = pickMemberList(candidate);
      if (nestedMembers.length > 0) {
        return nestedMembers;
      }
    }
  }

  return [];
};

export async function getCurrentMemberUser(options?: ApiRequestOptions) {
  const member = await request<CurrentMember>('/bff/iam-ui/members/me', {
    method: 'GET',
    ...(options || {}),
  });

  const userUuid = pickFirstNonEmpty(member.userUuid, member.userId, member.id);
  const nickname = pickFirstNonEmpty(
    member.nickname,
    member.name,
    member.username,
  );
  const tenantUuid = pickFirstNonEmpty(member.tenantUuid);
  const organizationUuid = pickFirstNonEmpty(
    member.organizationUuid,
    member.orgs?.[0]?.orgId,
    member.orgs?.[0]?.nodeId,
  );

  return {
    id: userUuid || '',
    uuid: userUuid || '',
    username: member.username,
    nickname,
    name: member.name,
    phone: member.phone,
    email: member.email,
    avatar: member.avatarUrl || member.photo,
    tenantUuid: tenantUuid || '',
    organizationUuid: organizationUuid || '',
    memberUuid: member.memberUuid,
    lastLoginOrganizationUuid: organizationUuid || '',
  } satisfies CurrentUser;
}

export async function listTenantMembers(options?: ApiRequestOptions) {
  const response = await tenantManagementRequest<MemberListResponse>(
    '/bff/iam-ui/members',
    {
      method: 'GET',
      params: {
        pageNo: 1,
        pageSize: 200,
        includeChildrenNodes: true,
      },
      ...(options || {}),
    },
  );

  return pickMemberList(response);
}

export async function refreshToken(
  refreshTokenValue: string,
  ssoId?: string,
  options?: ApiRequestOptions,
) {
  return request<OAuth2TokenResult>('/bff/auth/refreshToken', {
    method: 'POST',
    params: {
      refreshToken: refreshTokenValue,
      sso_id: ssoId,
    },
    ...(options || {}),
  });
}

export async function exchangeCodeForToken(
  body: ExchangeCodeForTokenPayload,
  options?: ApiRequestOptions,
) {
  return request<OAuth2TokenResult>('/bff/auth/codeLogin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function getTenantList(options?: ApiRequestOptions) {
  return request<TenantContext[]>('/bff/auth/tenant-list', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getTenantByCode(
  code: string,
  options?: ApiRequestOptions,
) {
  return request<TenantByCodeResult>('/bff/auth/tenant-by-code', {
    method: 'GET',
    params: { code },
    ...(options || {}),
  });
}

export async function switchTenant(
  organizationUuid: string,
  options?: ApiRequestOptions,
) {
  return request<SwitchTenantResult>('/bff/auth/switch-tenant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { organizationUuid },
    ...(options || {}),
  });
}

export async function joinTenant(
  body: JoinTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<JoinTenantResult>('/bff/auth/join-tenant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function createTenant(
  body: CreateTenantPayload,
  options?: ApiRequestOptions,
) {
  return request<CreateTenantResult>('/bff/auth/tenant', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function deleteTenant(
  tenantUuid: string,
  options?: ApiRequestOptions,
) {
  return request<DeleteTenantResult>(`/bff/auth/tenant/${tenantUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function logout(ssoId?: string, options?: ApiRequestOptions) {
  return request<LogoutResult>('/bff/auth/logout', {
    method: 'POST',
    params: {
      sso_id: ssoId,
    },
    ...(options || {}),
  });
}
